import AssignmentIcon from "@mui/icons-material/Assignment";
import EditRounded from "@mui/icons-material/EditRounded";
import { CardHeader, Grid, Tab, Tooltip, Typography } from "@mui/material";
import { ICustomTab } from "@vmosolutions/oc-interfaces";
import { useEffect, useState } from "react";
import { ITabElement } from "./BetaEventEditor";

// Store any component reused throughout the tabs here as opposed to making copies. Helps keep styles unified and reduces code duplication! -- CC

interface ICopyToClipboard {
  copyValue: string;
  label?: string; // If label is undefined or empty string, no label wil be displayed.
  className?: string;
  containerSx?: string;
}

export const CopyToClipboard = (props: ICopyToClipboard): JSX.Element => {
  const { label, copyValue, className, containerSx } = props;
  if (!copyValue) return null;
  return (
    <Grid container className={className} sx={{ containerSx }} style={{ width: "auto" }}>
      {!!label && <Typography className="label">{label}</Typography>}
      {navigator?.clipboard && (
        <Tooltip enterDelay={0} placement="top" title="Copy value to clipboard">
          <AssignmentIcon
            className="icon"
            style={{ cursor: "pointer" }}
            onClick={() => {
              navigator.clipboard.writeText(copyValue);
            }}
          />
        </Tooltip>
      )}
    </Grid>
  );
};

// Render Form Header for currently selected tab.
interface IFormHeader {
  title: string;
  subtitle?: string;
}
export const FormHeader = (props: IFormHeader): JSX.Element => {
  const { title, subtitle } = props;
  return (
    <div className="form_header">
      <CardHeader title={title} className="form-title" titleTypographyProps={{ variant: "h6" }} />
      {!!subtitle && <span className="form-subtitle">{subtitle}</span>}
    </div>
  );
};

/**
 * Render Tab components.
 * @param label - Tab label string
 * @param dirty - Boolean status indicating current tab's form status.
 * @returns - Tab component
 */
export const FormTabWrapper = (label: string, dirty?: boolean): JSX.Element => {
  return dirty ? (
    <Tab
      wrapped={true}
      label={label}
      key={label + "-tab"}
      classes={{ root: "form-tab dirty" }}
      icon={<EditRounded />}
    />
  ) : (
    <Tab wrapped={true} key={label + "-tab"} label={label} classes={{ root: "form-tab" }} />
  );
};

/**
 * Inject custom tabs and their content into OcTabs in proper indexed order.
 * @param customFlightTabs - custom flight tabs stored in structures
 * @param tabs - default tabs defined in Event Editor wrapper.
 * @returns Newly indexed tabs with custom ones added.
 */
export const getEventEditorTabs = (
  customFlightTabs: ICustomTab[],
  tabs: ITabElement[],
  tabDirtyStates: any
): ITabElement[] => {
  const newTabs = [...tabs];
  for (const customFlightTab of customFlightTabs) {
    const tab = {
      wrapper: FormTabWrapper(customFlightTab.title, tabDirtyStates[customFlightTab.title]),
      id: customFlightTab.title,
    };
    const index = customFlightTab.tabIndex >= 0 ? customFlightTab.tabIndex : tabs.length;
    newTabs.splice(index, 0, tab);
  }
  return newTabs;
};

/**
 * Constant identifiers for Formik-based tabs that register with the save-all mechanism.
 * Custom tabs use their dynamic title as the identifier instead.
 */
export enum FormTabId {
  Flight = "Flight",
  Log = "Log",
  GroundEvent = "GroundEvent",
}

interface IFormIsDirty {
  updateParent: (state: boolean) => void;
  formDirtyState: boolean;
}

/**
 * Handle interface for tabs that register with the parent's save-all mechanism.
 * Each Formik-based tab registers its submitForm and isDirty functions so the
 * parent can trigger submission of all dirty forms when any single tab submits.
 */
export interface IFormHandle {
  submitForm: () => Promise<void>;
  isDirty: () => boolean;
}

/**
 * This small effect component takes in the form status of any Formik instance and monitors it's dirty status.
 * If that dirty status changes, it updates the parents wrapper so that the tabs can display an edit icon.
 * @param props -
 * @returns
 */
export const FormikFormIsDirtyEffect = (props: IFormIsDirty): JSX.Element => {
  const { updateParent, formDirtyState } = props;
  const [isDirty, setIsDirty] = useState<boolean>(false);
  useEffect(() => {
    if (formDirtyState !== isDirty) {
      // If form dirty status has changed, update state and fire off handler to update parent.
      setIsDirty(formDirtyState);
      updateParent(formDirtyState);
    }
  }, [formDirtyState, updateParent, isDirty]);
  return null;
};
